import { useRef, useEffect } from "react";
import { Eraser } from "lucide-react";

// Draw-by-hand signature (mouse, trackpad, or finger). Stores a PNG data URL.
export function SignaturePad({
  value,
  onChange,
  height = 130,
}: {
  value: string;
  onChange: (v: string) => void;
  height?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const sizeCanvas = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (w === 0 || h === 0) return;
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      if (valueRef.current) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, w, h);
        img.src = valueRef.current;
      }
    };

    sizeCanvas();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(sizeCanvas);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const point = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const startDraw = (e: React.PointerEvent) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };
  const moveDraw = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !last.current) return;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onPointerDown={startDraw}
        onPointerMove={moveDraw}
        onPointerUp={endDraw}
        onPointerLeave={endDraw}
        style={{ touchAction: "none", height }}
        className="w-full bg-white border border-slate-300 rounded-lg cursor-crosshair"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-[11px] text-slate-400">
          Sign above — mouse, trackpad, or finger
        </span>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <Eraser className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
    </div>
  );
}

// Read-only render for the report: shows the drawn signature, or typed text
// (older records stored a typed name), or a dash.
export function SignatureView({ value }: { value?: string }) {
  if (!value) return <span className="text-slate-400">—</span>;
  if ((value.startsWith("data:image") || /^https?:\/\//.test(value))) {
    // eslint-disable-next-line @next/next/no-img-element
    return (
      <img
        src={value}
        alt="signature"
        className="h-14 max-w-[220px] object-contain"
      />
    );
  }
  return <span className="text-slate-800">{value}</span>;
}
